import React from 'react';
import ProcedureNoteThreadPreview from '../../procedures/ProcedureNoteThreadPreview';
import { HistorySection } from './HistorySection';

/**
 * VisitProceduresHistorySection (Phase 2.5) — per-visit timeline of
 * sittings performed on a specific visit. Renders inside an expanded
 * visit, below Prescriptions and above Reports.
 *
 * For each sitting, shows:
 *   - Status badge (Completed / Cancelled / Awaiting payment / …)
 *   - Procedure name + cost
 *   - "By <name>" attribution:
 *       * `requires_approval` + `approver_member_id` set →
 *         show APPROVER (main doctor who signed off)
 *       * else → show assignee (who actually performed it)
 *   - Timestamp when the sitting reached its current status (approved_at
 *     / completed_at / cancelled_at / payment_cleared_at)
 */
export function VisitProceduresHistorySection({ sittings, loading, isDark, visitId, visitDate }) {
  // Readable date for the per-procedure Added/Completed chips (accepts a
  // YYYY-MM-DD visit date or a full ISO timestamp).
  const fmtVisitDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };
  if (loading) {
    return (
      <HistorySection title="🦷 Procedures" isDark={isDark}>
        <p style={{ fontSize: '0.6875rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
          Loading procedures…
        </p>
      </HistorySection>
    );
  }
  if (!sittings || sittings.length === 0) return null;

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d)) return '';
      return d.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit',
        hour12: true, day: '2-digit', month: 'short',
      });
    } catch { return ''; }
  };

  const STATUS_TEXT = {
    awaiting_payment: { label: 'Awaiting payment', bg: '#fef3c7', fg: '#92400e', darkBg: 'rgba(245,158,11,0.25)', darkFg: '#fcd34d' },
    payment_cleared: { label: 'Ready to start', bg: '#ccfbf1', fg: '#115e59', darkBg: 'rgba(13,148,136,0.25)', darkFg: '#5eead4' },
    scheduled: { label: 'Scheduled', bg: '#e0f2fe', fg: '#075985', darkBg: 'rgba(14,165,233,0.25)', darkFg: '#7dd3fc' },
    in_progress: { label: 'In progress', bg: '#dbeafe', fg: '#1e40af', darkBg: 'rgba(37,99,235,0.3)', darkFg: '#93c5fd' },
    pending_approval: { label: 'Awaiting approval', bg: '#ede9fe', fg: '#5b21b6', darkBg: 'rgba(124,58,237,0.3)', darkFg: '#c4b5fd' },
    completed: { label: 'Completed', bg: '#d1fae5', fg: '#065f46', darkBg: 'rgba(16,185,129,0.3)', darkFg: '#6ee7b7' },
    paid: { label: 'Paid', bg: '#d1fae5', fg: '#065f46', darkBg: 'rgba(16,185,129,0.3)', darkFg: '#6ee7b7' },
    cancelled: { label: 'Cancelled', bg: '#f3f4f6', fg: '#4b5563', darkBg: 'rgba(107,114,128,0.3)', darkFg: '#d1d5db' },
  };

  // Build the attribution segments for a sitting:
  //   - "Assigned to <name>" — who is meant to perform it (always, if known)
  //   - "by <assigner>"      — only when the assigner differs from the assignee
  //   - "approved by Dr. X"  — sign-off, when the sitting required approval
  //                            and has reached a terminal (completed/paid) state
  // Names are denormalized on the sitting row (assigned_to_name /
  // assigned_by_name); approver_name isn't denormalized yet, so fall back to
  // assigned_by_name (the plan creator is almost always the approver).
  const attributionOf = (s) => {
    const parts = [];
    const assigneeName = s.assigned_to_name || s.assigned_to?.name || '';
    const assigneeRole = s.assigned_to_role || s.assigned_to?.role || '';
    const assigneeLabel = assigneeName
      ? `${assigneeRole === 'DOCTOR' ? 'Dr. ' : ''}${assigneeName}`
      : '';
    if (assigneeLabel) parts.push(`Assigned to ${assigneeLabel}`);

    const assignerName = s.assigned_by_name || '';
    if (assignerName && assignerName !== assigneeName) {
      parts.push(`by ${assignerName}`);
    }

    const terminalSigned = s.status === 'completed' || s.status === 'paid';
    if (terminalSigned && s.requires_approval && s.approver_member_id) {
      const approverName =
        s.approver_name || s.assigned_by_name || s.approver_member_id;
      parts.push(`approved by Dr. ${approverName}`);
    }
    return parts;
  };

  // Pick the "current state" timestamp to display.
  const stamp = (s) => {
    if (s.status === 'cancelled') return s.cancelled_at;
    if (s.status === 'completed' || s.status === 'paid') return s.completed_at || s.approved_at;
    if (s.status === 'pending_approval') return s.completed_at;
    if (s.status === 'in_progress') return s.executed_at;
    if (s.status === 'payment_cleared') return s.payment_cleared_at;
    return s.created_at;
  };

  return (
    <HistorySection title="🦷 Procedures" isDark={isDark}>
      {sittings.map((s) => {
        const st = STATUS_TEXT[s.status] || STATUS_TEXT.scheduled;
        const attribution = attributionOf(s);
        return (
          <div key={s.sitting_id} style={{
            padding: '0.375rem 0.5rem',
            borderRadius: '0.375rem',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            marginBottom: '0.3125rem',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              flexWrap: 'wrap',
            }}>
              <span style={{
                fontSize: '0.5625rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.03em',
                padding: '0.1rem 0.4rem', borderRadius: '9999px',
                background: isDark ? st.darkBg : st.bg,
                color: isDark ? st.darkFg : st.fg,
              }}>
                {st.label}
              </span>
              <span style={{
                fontSize: '0.6875rem', fontWeight: 600,
                color: isDark ? '#e8e9ed' : '#111827',
              }}>
                {s.procedure_name || '(procedure)'}
              </span>
              {typeof s.final_cost !== 'undefined' && s.final_cost !== null && (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  color: isDark ? '#5eead4' : '#0f766e',
                }}>
                  ₹{Number(s.final_cost).toLocaleString('en-IN')}
                </span>
              )}
            </div>
            <div style={{
              marginTop: '0.2rem',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              flexWrap: 'wrap',
              fontSize: '0.5625rem',
              color: isDark ? '#9ca3af' : '#6b7280',
            }}>
              {attribution.length > 0 && <span>{attribution.join(' · ')}</span>}
              {stamp(s) && (
                <span>{attribution.length ? '· ' : ''}{fmtTime(stamp(s))}</span>
              )}
              {s.cancellation_reason && (
                <span style={{ color: '#b91c1c' }}>· {s.cancellation_reason}</span>
              )}
            </div>
            {/* Procedures-on-New-Visits — show the procedure's lifecycle from
                its OWN lineage: ADDED (the visit it was first added on) and,
                when terminal, COMPLETED (the visit it was completed on — the
                moved-to visit for a historical mirror, else its current visit). */}
            {(() => {
              const moved = s.is_historical && s.moved_to_visit_id;
              const terminal = s.status === 'completed' || s.status === 'paid';
              const originDate = s.origin_suggested_at
                ? String(s.origin_suggested_at).slice(0, 10) : null;
              // New rows carry a concrete `expires_at` (advised + 30d by default,
              // doctor-adjustable); expired = its date is past (inclusive of the
              // date itself). Legacy rows (no `expires_at`) fall back to the old
              // awaiting_payment + ~1-month rule.
              const todayStr = new Date().toISOString().slice(0, 10);
              const isExpired = s.expires_at
                ? String(s.expires_at).slice(0, 10) < todayStr
                : (s.status === 'awaiting_payment' && originDate
                  && (Date.now() - new Date(originDate).getTime() > 31 * 24 * 3600 * 1000));
              const addId = s.origin_visit_id || s.visit_id || visitId;
              const addDate = originDate || s.visit_date || visitDate;
              const doneId = moved ? s.moved_to_visit_id : s.visit_id;
              const doneDate = moved ? s.moved_to_visit_date : (s.completed_at || s.visit_date || visitDate);
              // Clean id chip: label + monospace last-6 of the visit id + date.
              const chip = (label, vid, date, bg, fg) => (
                <span style={{
                  display: 'inline-flex', alignItems: 'baseline', gap: '0.25rem',
                  fontSize: '0.5625rem', fontWeight: 600, padding: '0.12rem 0.45rem',
                  borderRadius: '0.375rem', background: bg, color: fg,
                  marginRight: '0.3rem', marginTop: '0.15rem',
                }}>
                  {label}
                  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.02em' }}>
                    {String(vid).slice(-6)}
                  </span>
                  {date && <span style={{ fontWeight: 500, opacity: 0.85 }}>· {fmtVisitDate(date)}</span>}
                </span>
              );
              return (
                <div style={{ marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                  {addId && chip('Added · Visit', addId, addDate,
                    isDark ? 'rgba(13,148,136,0.22)' : '#ccfbf1', isDark ? '#5eead4' : '#115e59')}
                  {terminal && doneId && chip('Completed · Visit', doneId, doneDate,
                    isDark ? 'rgba(16,185,129,0.25)' : '#d1fae5', isDark ? '#6ee7b7' : '#065f46')}
                  {isExpired && (
                    <span style={{
                      fontSize: '0.5625rem', fontWeight: 600, padding: '0.12rem 0.45rem',
                      borderRadius: '0.375rem', marginTop: '0.15rem',
                      background: isDark ? 'rgba(148,163,184,0.25)' : '#f1f5f9',
                      color: isDark ? '#94a3b8' : '#64748b',
                    }}>Expired suggestion</span>
                  )}
                </div>
              );
            })()}
            {/* Full procedure note thread (renders null when no notes). */}
            <div style={{ marginTop: '0.3rem' }}>
              <ProcedureNoteThreadPreview sitting={s} />
            </div>
          </div>
        );
      })}
    </HistorySection>
  );
}
